/**
 * SAIF SYNC — client wrapper for the Cloudflare Worker backend (via Shopify App Proxy).
 *
 * Calls go to the storefront-relative App Proxy path (default /apps/saif).
 * Shopify HMAC-signs the request with our app's client_secret and injects
 * `logged_in_customer_id`. The Worker verifies the signature and trusts the
 * customer ID — no client-side auth token needed.
 *
 * If the App Proxy isn't configured yet, this falls back to localStorage so
 * the storefront keeps working unchanged.
 *
 * Configuration is read from <script id="SaifSyncConfig" type="application/json">,
 * emitted by layout/theme.liquid when the customer is logged in.
 */
(function (global) {
  'use strict';

  // ----- Config -----
  var configEl = document.getElementById('SaifSyncConfig');
  var config = null;
  if (configEl) {
    try { config = JSON.parse(configEl.textContent); }
    catch (e) { console.error('[saif-sync] config parse failed:', e); }
  }
  var ready = !!(config && config.proxyPath && config.customerId);
  // The App Proxy is reachable even for logged-out visitors (Shopify still
  // HMAC-signs the request, just without logged_in_customer_id). `hasProxy`
  // gates the few endpoints that are allowed anonymously — chiefly the
  // Practice Studio entitlement / one-free-try gate.
  var hasProxy = !!(config && config.proxyPath);

  function lsKey(scope, lessonId) {
    var cid = (config && config.customerId) || 'anon';
    return 'saif_' + scope + '_' + cid + (lessonId ? '_' + lessonId : '');
  }

  /* ----- takeBlobs — keep signed-out audio across reloads -----------------
     A signed-out take used to be stored as an object URL, which dies with the
     document, so refreshing left an unplayable row. Park the actual blob here
     and mint a fresh URL on each load. Every call resolves (never rejects) —
     where IndexedDB is unavailable, callers simply get nothing back and the
     old "audio not kept" treatment applies. */
  var IDB_NAME = 'saif-takes', IDB_STORE = 'blobs';
  function idbOpen() {
    return new Promise(function (resolve) {
      try {
        if (typeof indexedDB === 'undefined' || !indexedDB) return resolve(null);
        var rq = indexedDB.open(IDB_NAME, 1);
        rq.onupgradeneeded = function () {
          try { if (!rq.result.objectStoreNames.contains(IDB_STORE)) rq.result.createObjectStore(IDB_STORE); } catch (e) {}
        };
        rq.onsuccess = function () { resolve(rq.result || null); };
        rq.onerror = function () { resolve(null); };
        rq.onblocked = function () { resolve(null); };
      } catch (e) { resolve(null); }
    });
  }
  function idbPut(id, blob) {
    return idbOpen().then(function (db) {
      if (!db) return false;
      return new Promise(function (resolve) {
        try {
          var tx = db.transaction(IDB_STORE, 'readwrite');
          tx.objectStore(IDB_STORE).put(blob, String(id));
          tx.oncomplete = function () { resolve(true); };
          tx.onerror = function () { resolve(false); };
          tx.onabort = function () { resolve(false); };
        } catch (e) { resolve(false); }
      });
    });
  }
  function idbGet(id) {
    return idbOpen().then(function (db) {
      if (!db) return null;
      return new Promise(function (resolve) {
        try {
          var rq = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(String(id));
          rq.onsuccess = function () { resolve(rq.result || null); };
          rq.onerror = function () { resolve(null); };
        } catch (e) { resolve(null); }
      });
    });
  }
  function idbDelete(ids) {
    return idbOpen().then(function (db) {
      if (!db) return false;
      return new Promise(function (resolve) {
        try {
          var tx = db.transaction(IDB_STORE, 'readwrite');
          var store = tx.objectStore(IDB_STORE);
          (ids || []).forEach(function (i) { try { store.delete(String(i)); } catch (e) {} });
          tx.oncomplete = function () { resolve(true); };
          tx.onerror = function () { resolve(false); };
          tx.onabort = function () { resolve(false); };
        } catch (e) { resolve(false); }
      });
    });
  }

  // ----- Core HTTP -----
  /**
   * Make a request to a route on our App Proxy.
   * - method: 'GET' or 'POST'
   * - route: path under proxyPath, e.g. '/notes/save'
   * - body: object (POST only)
   * - timeoutMs: defaults to 12000, override for slow ops like uploads
   */
  function request(method, route, body, timeoutMs, requireCustomer) {
    // Most routes need a signed-in customer. Pass requireCustomer === false for
    // the handful that work anonymously (they only need the proxy to be live).
    var needCustomer = requireCustomer !== false;
    if (needCustomer && !ready) return Promise.resolve({ ok: false, error: 'NOT_CONFIGURED' });
    if (!hasProxy) return Promise.resolve({ ok: false, error: 'NOT_CONFIGURED' });

    var url = config.proxyPath.replace(/\/$/, '') + route;
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (ctrl) ctrl.abort();
    }, typeof timeoutMs === 'number' ? timeoutMs : 12000);

    var init = {
      method: method,
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    };
    if (ctrl) init.signal = ctrl.signal;
    if (body && method !== 'GET') {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    return fetch(url, init)
      .then(function (resp) {
        clearTimeout(timer);
        if (!resp.ok) {
          // Try to read the JSON body so callers (and the console) see the
          // worker's actual error code + detail instead of a bare HTTP 500.
          return resp.json().then(function (body) {
            console.error('[saif-sync] HTTP ' + resp.status + ' for ' + method + ' ' + route, body);
            return { ok: false, error: (body && body.error) || ('HTTP_' + resp.status), detail: body && body.detail, status: resp.status };
          }).catch(function () {
            console.error('[saif-sync] HTTP ' + resp.status + ' for ' + method + ' ' + route);
            return { ok: false, error: 'HTTP_' + resp.status };
          });
        }
        return resp.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        var code = (err && err.name === 'AbortError') ? 'TIMEOUT' : 'NETWORK_ERROR';
        console.error('[saif-sync] ' + code + ' for ' + method + ' ' + route, err && err.message);
        return { ok: false, error: code, detail: err && err.message };
      });
  }

  // ----- Notes -----
  var Notes = {
    getAll: function () {
      if (!ready) {
        var out = {};
        var prefix = lsKey('note', '');
        for (var i = 0; i < localStorage.length; i++) {
          var k = localStorage.key(i);
          if (k && k.indexOf(prefix) === 0) {
            out[k.slice(prefix.length)] = localStorage.getItem(k);
          }
        }
        return Promise.resolve({ ok: true, notes: out, source: 'local' });
      }
      return request('GET', '/notes');
    },
    save: function (lessonId, text) {
      if (!ready) {
        try {
          if (text === '' || text == null) localStorage.removeItem(lsKey('note', lessonId));
          else localStorage.setItem(lsKey('note', lessonId), text);
          return Promise.resolve({ ok: true, source: 'local' });
        } catch (e) {
          return Promise.resolve({ ok: false, error: 'STORAGE_FULL' });
        }
      }
      return request('POST', '/notes/save', { lessonId: lessonId, text: text });
    }
  };

  // ----- Progress -----
  function defaultProgress() {
    return { completed: [], lastSeen: {}, streak: { count: 0, longest: 0, lastDay: null } };
  }
  var Progress = {
    get: function () {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('progress', ''));
        return Promise.resolve({
          ok: true,
          progress: raw ? safeParse(raw, defaultProgress()) : defaultProgress(),
          source: 'local'
        });
      }
      return request('GET', '/progress');
    },
    markComplete: function (lessonId) {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('progress', ''));
        var p = raw ? safeParse(raw, defaultProgress()) : defaultProgress();
        if (p.completed.indexOf(lessonId) === -1) p.completed.push(lessonId);
        p.lastSeen[lessonId] = new Date().toISOString();
        var today = new Date().toISOString().slice(0, 10);
        var yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        if (p.streak.lastDay !== today) {
          p.streak.count = (p.streak.lastDay === yesterday) ? p.streak.count + 1 : 1;
          p.streak.longest = Math.max(p.streak.longest || 0, p.streak.count);
          p.streak.lastDay = today;
        }
        try { localStorage.setItem(lsKey('progress', ''), JSON.stringify(p)); } catch (e) {}
        return Promise.resolve({ ok: true, progress: p, source: 'local' });
      }
      return request('POST', '/progress/markComplete', { lessonId: lessonId });
    }
  };

  // ----- Recordings -----
  var Recordings = {
    // opts.full=true asks the server for the heavy pitch-contour arrays
    // (only the comparison chart needs them). The default list is a
    // lightweight metadata index — score/breakdown/title/date, without the
    // large pitch-contour arrays.
    list: function (opts) {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('recordings', ''));
        var recs = raw ? safeParse(raw, []) : [];
        // Re-mint a usable URL for each take whose audio we still hold. The
        // one saved in localStorage died with the document that created it.
        return Promise.all(recs.map(function (rec) {
          if (!rec || !rec.ephemeral) return Promise.resolve(rec);
          return idbGet(rec.id).then(function (blob) {
            if (!blob) return rec;
            try {
              rec.url = URL.createObjectURL(blob);
              rec.audioRestored = true;   // studio: render a real play button
            } catch (e) {}
            return rec;
          });
        })).then(function (out) {
          return { ok: true, recordings: out, source: 'local' };
        });
      }
      var route = (opts && opts.full) ? '/recordings?full=1' : '/recordings';
      return request('GET', route, null, 20000);
    },
    /**
     * Save a recording. blob is a Blob (audio/webm or similar). metadata has
     * { lessonId, score, breakdown, duration, pitchData }.
     */
    save: function (blob, metadata) {
      if (!ready) {
        var url = URL.createObjectURL(blob);
        var raw = localStorage.getItem(lsKey('recordings', ''));
        var arr = raw ? safeParse(raw, []) : [];
        var entry = {
          id: 'local_' + Date.now(),
          url: url,
          mimeType: blob.type,
          createdAt: new Date().toISOString(),
          ephemeral: true,
          lessonId: metadata && metadata.lessonId,
          score: metadata && metadata.score,
          breakdown: (metadata && metadata.breakdown) || null,
          clipTitle: (metadata && metadata.clipTitle) || null,
          duration: metadata && metadata.duration,
          referenceUrl: (metadata && metadata.referenceUrl) || null,
          submittedForFeedback: !!(metadata && metadata.submittedForFeedback)
        };
        arr.unshift(entry);
        // Bound the device-local fallback list. Signed-in saves go to the
        // server (capped there); this only accrues for anonymous single-take
        // sessions, but cap it so a logged-out visitor can't grow it forever.
        var dropped = [];
        if (arr.length > 30) { dropped = arr.slice(30).map(function (r) { return r.id; }); arr = arr.slice(0, 30); }
        try { localStorage.setItem(lsKey('recordings', ''), JSON.stringify(arr)); } catch (e) {}
        // Park the audio itself so the take is still playable after a reload,
        // and evict the blobs of anything the cap just dropped.
        return idbPut(entry.id, blob).then(function (stored) {
          entry.audioRestored = !!stored;
          if (dropped.length) return idbDelete(dropped).then(function () { return stored; });
          return stored;
        }).then(function () {
          return { ok: true, recording: entry, source: 'local' };
        });
      }
      return blobToBase64(blob).then(function (b64) {
        return request('POST', '/recordings/save', {
          audioBase64: b64,
          mimeType: blob.type || 'audio/webm',
          lessonId: (metadata && metadata.lessonId) || null,
          score: (metadata && metadata.score) || null,
          breakdown: (metadata && metadata.breakdown) || null,
          clipTitle: (metadata && metadata.clipTitle) || null,
          duration: (metadata && metadata.duration) || null,
          pitchData: (metadata && metadata.pitchData) || null,
          referencePitchData: (metadata && metadata.referencePitchData) || null,
          referenceUrl: (metadata && metadata.referenceUrl) || null,
          submittedForFeedback: !!(metadata && metadata.submittedForFeedback)
        }, 60000);
      });
    },
    /**
     * Submit (or withdraw) a saved recording for instructor feedback.
     * submitted defaults true; pass false to withdraw. referenceUrl is an
     * optional backfill for older takes saved before the reference was stored.
     * Requires sign-in — local-only recordings can't reach an instructor.
     */
    submit: function (id, submitted, referenceUrl, note) {
      if (!ready) {
        // Do NOT record a submission that never happened. This used to set
        // submittedForFeedback = true "so the UI updates" and then return a
        // failure — so after a reload the history row said "Submitted" for a
        // take that had never left the device, and no request was ever made.
        // Report the failure and leave the stored take exactly as it was.
        var raw = localStorage.getItem(lsKey('recordings', ''));
        var arr = raw ? safeParse(raw, []) : [];
        var found = arr.some(function (r) { return r.id === id; });
        return Promise.resolve({ ok: false, error: 'NOT_SIGNED_IN', local: found, source: 'local' });
      }
      return request('POST', '/recordings/submit', {
        recordingId: String(id),
        submitted: submitted !== false,
        referenceUrl: referenceUrl || null,
        note: (note != null ? String(note) : null)
      }, 15000);
    },
    delete: function (id) {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('recordings', ''));
        var arr = raw ? safeParse(raw, []) : [];
        arr = arr.filter(function (r) { return r.id !== id; });
        try { localStorage.setItem(lsKey('recordings', ''), JSON.stringify(arr)); } catch (e) {}
        return idbDelete([id]).then(function () { return { ok: true, source: 'local' }; });
      }
      return request('POST', '/recordings/delete', { id: id });
    },
    /**
     * Delete many recordings atomically (one server-side read-modify-write).
     * Avoids the race that N concurrent single deletes hit on the shared
     * recordings metafield (only the last write would stick).
     */
    deleteMany: function (ids) {
      ids = ids || [];
      if (!ready) {
        var raw = localStorage.getItem(lsKey('recordings', ''));
        var arr = raw ? safeParse(raw, []) : [];
        var idset = {};
        ids.forEach(function (i) { idset[i] = 1; });
        arr = arr.filter(function (r) { return !idset[r.id]; });
        try { localStorage.setItem(lsKey('recordings', ''), JSON.stringify(arr)); } catch (e) {}
        return Promise.resolve({ ok: true, deleted: ids.length, source: 'local' });
      }
      return request('POST', '/recordings/deleteMany', { ids: ids }, 30000);
    },
    /**
     * Submit many recordings for instructor feedback atomically, with one
     * optional note attached to each. Requires sign-in.
     */
    submitMany: function (ids, note) {
      ids = ids || [];
      if (!ready) {
        return Promise.resolve({ ok: false, error: 'NOT_SIGNED_IN', source: 'local' });
      }
      return request('POST', '/recordings/submitMany', {
        ids: ids,
        note: (note != null ? String(note) : null)
      }, 30000);
    }
  };

  // ----- Badges -----
  var Badges = {
    get: function () {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('badges', ''));
        return Promise.resolve({ ok: true, badges: raw ? safeParse(raw, []) : [], source: 'local' });
      }
      return request('GET', '/badges');
    },
    add: function (id) {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('badges', ''));
        var arr = raw ? safeParse(raw, []) : [];
        if (arr.indexOf(id) === -1) arr.push(id);
        try { localStorage.setItem(lsKey('badges', ''), JSON.stringify(arr)); } catch (e) {}
        return Promise.resolve({ ok: true, badges: arr, source: 'local' });
      }
      return request('POST', '/badges/add', { id: id });
    }
  };

  // ----- Vocal Range -----
  // A logged-out visitor's tested range lives in localStorage only (v1). Once
  // signed in it's stored server-side; a signed-in student simply retests.
  var VocalRange = {
    get: function () {
      if (!ready) {
        var raw = localStorage.getItem(lsKey('vocal_range', ''));
        return Promise.resolve({ ok: true, range: raw ? safeParse(raw, null) : null, source: 'local' });
      }
      return request('GET', '/vocal-range');
    },
    save: function (range) {
      var lowMidi = Number(range && range.lowMidi);
      var highMidi = Number(range && range.highMidi);
      if (!ready) {
        var blob = {
          v: 1,
          lowMidi: Math.round(lowMidi * 10) / 10,
          highMidi: Math.round(highMidi * 10) / 10,
          testedAt: new Date().toISOString()
        };
        try { localStorage.setItem(lsKey('vocal_range', ''), JSON.stringify(blob)); } catch (e) {}
        return Promise.resolve({ ok: true, range: blob, source: 'local' });
      }
      return request('POST', '/vocal-range/save', { lowMidi: lowMidi, highMidi: highMidi });
    }
  };

  // ----- Bookings -----
  var Bookings = {
    list: function () {
      if (!ready) return Promise.resolve({ ok: true, upcoming: [], past: [], source: 'local' });
      return request('GET', '/bookings');
    },
    requestReschedule: function (bookingId, requestedDate, requestedTime, reason) {
      if (!ready) return Promise.resolve({ ok: false, error: 'NOT_CONFIGURED' });
      return request('POST', '/bookings/requestReschedule', {
        bookingId: String(bookingId),
        requestedDate: requestedDate || null,
        requestedTime: requestedTime || null,
        reason: String(reason || '')
      });
    }
  };

  // ----- Instructor -----
  var Instructor = {
    listStudents: function (pageInfo, pageSize) {
      var qs = '?pageSize=' + (pageSize || 50);
      if (pageInfo) qs += '&pageInfo=' + encodeURIComponent(pageInfo);
      return request('GET', '/instructor/students' + qs, null, 20000);
    },
    getStudent: function (customerId) {
      return request('POST', '/instructor/student', { customerId: String(customerId) }, 15000);
    },
    leaveFeedback: function (customerId, recordingId, text, audio) {
      // audio (optional): { base64: string, mimeType: string } — instructor
      // voice note, stored in R2 and streamed back to the student.
      var body = {
        customerId: String(customerId),
        recordingId: String(recordingId),
        text: String(text || '')
      };
      if (audio && audio.base64) {
        body.audioBase64 = audio.base64;
        body.audioMimeType = audio.mimeType || 'audio/webm';
      }
      return request('POST', '/instructor/feedback', body, 30000);
    },
    communityPending: function () {
      return request('GET', '/instructor/community-pending', null, 20000);
    },
    communityModerate: function (id, action) {
      return request('POST', '/instructor/community-moderate', { id: String(id), action: String(action) });
    },
    listBookings: function () {
      return request('GET', '/instructor/bookings', null, 20000);
    }
  };

  /* SAIF_STUDIO_DEVICE_V1 — this browser's own id for the free-take allowance.

     The allowance used to be counted against the visitor's IP ADDRESS, which everybody behind
     a mobile carrier, a school or an office shares. 53 of 181 addresses had been exhausted, so
     roughly a third of arrivals met "You've used your free takes" on the free lesson page
     having never recorded once, and the year-long expiry meant that share could only grow.

     An id kept here is per browser, which is the closest honest stand-in for per person. It is
     forgeable by anyone who cares to look, and that is fine: this is a free demo, not a
     security boundary, and the worker keeps a loose per-address daily cap for abuse. */
  var DEVICE_KEY = 'saif:practice:device';
  function deviceId() {
    try {
      var id = localStorage.getItem(DEVICE_KEY);
      if (id && /^[a-z0-9]{8,64}$/.test(id)) return id;
      /* crypto.randomUUID is absent on older Safari; the fallback only has to be unlikely to
         collide, not unguessable. */
      var fresh = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID().replace(/-/g, '')
        : (Date.now().toString(36) + Math.random().toString(36).slice(2) +
           Math.random().toString(36).slice(2)).replace(/[^a-z0-9]/g, '').slice(0, 32);
      localStorage.setItem(DEVICE_KEY, fresh);
      return fresh;
    } catch (e) {
      /* Private mode, or storage refused. The worker treats a missing id as "cannot identify
         this browser" and gives them the daily allowance rather than a stranger's spent one. */
      return null;
    }
  }
  function withDevice(route) {
    var id = deviceId();
    return id ? route + (route.indexOf('?') === -1 ? '?' : '&') + 'dev=' + encodeURIComponent(id) : route;
  }

  // ----- Practice Studio entitlement / one-free-try gate -----
  // Server-authoritative when the proxy is live (per account for logged-in,
  // per DEVICE for anonymous). Falls back to a local one-try gate only when there
  // is no backend at all, so the studio still behaves in local-only/dev mode.
  function localStudioStatus() {
    var used = false;
    try { used = !!localStorage.getItem(lsKey('studio_used', '')); } catch (e) {}
    return {
      ok: true,
      access: used ? 'locked' : 'preview',
      reason: used ? 'used' : 'free_try',
      loggedIn: !!(config && config.customerId),
      canStartTrial: false,
      triesRemaining: used ? 0 : 1,
      trialEndsAt: null,
      grantEndsAt: null,
      source: 'local'
    };
  }
  function markLocalStudioUsed() {
    try { localStorage.setItem(lsKey('studio_used', ''), new Date().toISOString()); } catch (e) {}
  }
  var Studio = {
    status: function () {
      if (!hasProxy) return Promise.resolve(localStudioStatus());
      return request('GET', withDevice('/studio/status'), null, 12000, false);
    },
    // Consume the one free take. Idempotent server-side.
    use: function () {
      if (!hasProxy) { markLocalStudioUsed(); return Promise.resolve(localStudioStatus()); }
      return request('POST', withDevice('/studio/use'), {}, 12000, false);
    },
    // Start the account-linked free trial. Requires sign-in — there is no
    // anonymous trial (the trial must be tied to a Shopify customer).
    startTrial: function () {
      if (!ready) return Promise.resolve({ ok: false, error: 'NOT_SIGNED_IN' });
      return request('POST', '/studio/trial/start', {}, 12000);
    },
    // Remaining Personal Review credits for the signed-in student. (submit/list
    // responses also carry reviewCreditsRemaining, so this is only needed for a
    // standalone refresh.)
    reviewCredits: function () {
      if (!ready) return Promise.resolve({ ok: true, balance: 0 });
      return request('GET', '/studio/review-credits', null, 12000);
    }
  };

  // ----- Helpers -----
  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var s = reader.result || '';
        var i = s.indexOf(',');
        resolve(i >= 0 ? s.slice(i + 1) : s);
      };
      reader.onerror = function () { reject(reader.error); };
      reader.readAsDataURL(blob);
    });
  }
  function safeParse(s, fb) { try { return JSON.parse(s); } catch (e) { return fb; } }

  // ----- Public API -----
  global.SaifSync = {
    ready: ready,
    hasProxy: hasProxy,
    config: config,
    notes: Notes,
    progress: Progress,
    recordings: Recordings,
    badges: Badges,
    vocalRange: VocalRange,
    bookings: Bookings,
    instructor: Instructor,
    studio: Studio
  };

  if (!ready) {
    console.info('[saif-sync] backend not configured — running in local-only mode');
  } else {
    console.info('[saif-sync] ready, customer ' + config.customerId + ', proxy ' + config.proxyPath);
  }
})(window);
